import { getSceneActions, getSceneState } from "../stores/sceneStore";
import { logger } from "./logger";

const PRIMARY_CAMERA_NAME = "primary";
const WRIST_CAMERA_NAME = "wrist";

const GENERIC_PRIMARY_ORIENTATION = [
  0.9932789246695279, -0.10790462192185483, 0.04163072152185469, -0.004522543622522072,
];

const WRIST_SITE_OFFSET = [-0.0733, 0.0, 0.0413];
const WRIST_SITE_ORIENTATION = [
  0.6417231216503952, 0.2969704280551721, -0.295476641519321, -0.6424122930925767,
];

const WRIST_BODY_OFFSET = [0, 0.08, 0];
const WRIST_BODY_ORIENTATION = [-0.21643961393810282, 0.9762960071199335, 0, 0];

const toWorldBodyUuid = (worldBody) => {
  if (!worldBody) return null;
  if (typeof worldBody === "string") return worldBody;
  if (typeof worldBody?.uuid === "string") return worldBody.uuid;
  return null;
};

const removeNamedCameras = (sceneActions, sceneState, names) => {
  const targetNames = new Set(
    (names || []).map((name) => String(name || "").trim()).filter(Boolean)
  );
  Object.values(sceneState.cameras || {})
    .filter((camera) => targetNames.has(String(camera?.name || "").trim()))
    .forEach((camera) => {
      if (camera?.uuid) sceneActions.removeCamera(camera.uuid);
    });
};

const getGripperTargetSiteUuid = (sites) =>
  Object.values(sites || {}).find((site) => site?.name === "gripper_target")?.uuid || null;

const inferWristBodyUuid = (bodies, gripperTargetSiteUuid) => {
  if (gripperTargetSiteUuid) {
    const fromSiteParent = Object.values(bodies || {}).find(
      (body) => Array.isArray(body?.sites) && body.sites.includes(gripperTargetSiteUuid)
    )?.uuid;
    if (fromSiteParent) return fromSiteParent;
  }

  const preferredWristBodyNameRegexes = [
    /^wrist_3_link$/i,
    /^tool0$/i,
    /^ee_link$/i,
    /^gripper_base$/i,
    /^hand$/i,
    /wrist/i,
    /gripper/i,
    /tool/i,
    /\bee\b/i,
    /hand/i,
  ];
  const bodyEntries = Object.values(bodies || {});
  return (
    preferredWristBodyNameRegexes
      .map((regex) => bodyEntries.find((body) => regex.test(body?.name || ""))?.uuid)
      .find(Boolean) || null
  );
};

const addGenericPrimaryCamera = (sceneActions, sceneState) => {
  const rootUuid = toWorldBodyUuid((sceneState.worldBodies || [])[0]);
  const rootBody = rootUuid ? sceneState.bodies?.[rootUuid] : null;
  const rootPos = Array.isArray(rootBody?.position) ? rootBody.position : [0, 0, 0];
  const rx = Number(rootPos[0]) || 0;
  const ry = Number(rootPos[1]) || 0;
  const rz = Number(rootPos[2]) || 0;

  sceneActions.addCamera({
    name: PRIMARY_CAMERA_NAME,
    kind: "fixed",
    position: [rx + 0.35, ry + 0.35, Math.max(0.6, rz + 0.85)],
    orientation: GENERIC_PRIMARY_ORIENTATION,
    fovy: 65,
    width: 256,
    height: 256,
    fps: 8,
  });
};

const addGenericWristCamera = (sceneActions, sceneState, logTag) => {
  const bodies = sceneState.bodies || {};
  const sites = sceneState.sites || {};
  const gripperTargetSiteUuid = getGripperTargetSiteUuid(sites);

  if (gripperTargetSiteUuid) {
    sceneActions.addCamera({
      name: WRIST_CAMERA_NAME,
      kind: "mounted",
      mountType: "site",
      mountSiteUuid: gripperTargetSiteUuid,
      position: WRIST_SITE_OFFSET,
      orientation: WRIST_SITE_ORIENTATION,
      fovy: 65,
      width: 256,
      height: 256,
      fps: 8,
    });
    return;
  }

  const wristBodyUuid = inferWristBodyUuid(bodies, null);
  if (!wristBodyUuid) {
    logger.warn(`[${logTag}] Could not infer wrist mount; wrist camera not added.`);
    return;
  }

  sceneActions.addCamera({
    name: WRIST_CAMERA_NAME,
    kind: "mounted",
    mountType: "body",
    mountBodyUuid: wristBodyUuid,
    position: WRIST_BODY_OFFSET,
    orientation: WRIST_BODY_ORIENTATION,
    fovy: 65,
    width: 256,
    height: 256,
    fps: 8,
  });
};

export const ensureDefaultCamerasForLoadedPreset = ({ presetFolder, logTag = "preset_loader" }) => {
  const folder = String(presetFolder || "").trim();
  const sceneActions = getSceneActions();
  const sceneState = getSceneState();
  removeNamedCameras(sceneActions, sceneState, [PRIMARY_CAMERA_NAME, WRIST_CAMERA_NAME]);

  if (folder === "ur5e" || folder === "ur5e_critter") {
    sceneActions.addCamera({
      name: PRIMARY_CAMERA_NAME,
      kind: "fixed",
      position: [-0.3103, 1.4638, 0.9041],
      orientation: [
        0.8944908346926562, -0.43838108453988733, -0.0582088104339458, 0.06572598996083916,
      ],
      fovy: 65,
      width: 256,
      height: 256,
      fps: 8,
    });

    const wristBodyUuid = inferWristBodyUuid(
      sceneState.bodies || {},
      getGripperTargetSiteUuid(sceneState.sites)
    );
    if (wristBodyUuid) {
      sceneActions.addCamera({
        name: WRIST_CAMERA_NAME,
        kind: "mounted",
        mountType: "body",
        mountBodyUuid: wristBodyUuid,
        position: WRIST_BODY_OFFSET,
        orientation: WRIST_BODY_ORIENTATION,
        fovy: 65,
        width: 256,
        height: 256,
        fps: 8,
      });
    } else {
      logger.warn(`[${logTag}] Could not infer UR5e wrist mount body.`);
    }
    return;
  }

  if (folder === "trossen_wx250s") {
    addGenericPrimaryCamera(sceneActions, sceneState);
    addGenericWristCamera(sceneActions, sceneState, logTag);
    return;
  }

  // Fallback for every other preset scene.
  addGenericPrimaryCamera(sceneActions, sceneState);
  addGenericWristCamera(sceneActions, sceneState, logTag);
};
